// tele_frontend_2/src/views/admin/settings/components/GoogleCalendarWidget.jsx#L1-400
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Tag,
  Spinner,
  useToast,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "components-lib";
import { MdLink, MdLinkOff, MdRefresh, MdWarning } from "react-icons/md";
import Card from "components/card/Card";
import { getAuthStatus } from "api/googleAuthAPI";
import useGoogleAuth from "hooks/useGoogleAuth";

/**
 * GoogleCalendarWidget
 *
 * A modal component for managing Google Calendar authentication for a specific email.
 * Accepts email prop to fetch status for that account.
 * Controlled modal: requires isOpen and onClose props.
 */

export default function GoogleCalendarWidget({ isOpen, onClose, email, clinicId = null }) {
  // Determine whether the current user is an admin (read from localStorage 'user' object).
  let isAdmin = false;
  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    isAdmin =
      (storedUser && storedUser.role === "Admin") ||
      storedUser.role === "admin";
  } catch (e) {
    isAdmin = false;
  }

  const toast = useToast();

  const [status, setStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // useGoogleAuth handles popup flow and token revoke
  const { initiateAuth, revokeAuth, isAuthLoading } = useGoogleAuth({
    onSuccess: () => {
      // Refresh status after a successful auth/revoke action
      fetchStatus();
    },
    onError: (err) => {
      // Hook will show toasts; nothing extra required here.
    },
  });

  const fetchStatus = useCallback(async () => {
    if (!email) return;
    setIsLoadingStatus(true);
    try {
      const res = await getAuthStatus({ email }); // Assuming getAuthStatus can accept email param
      setStatus(res.data || null);
    } catch (err) {
      const message =
        err?.data?.message ||
        err?.message ||
        "Could not fetch Google auth status.";
      toast({
        title: "Unable to fetch status",
        description: message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      setStatus(null);
    } finally {
      setIsLoadingStatus(false);
      setIsFirstLoad(false);
    }
  }, [toast, email]);

  useEffect(() => {
    if (isOpen && email) {
      fetchStatus();
    }
  }, [fetchStatus, isOpen, email]);

  const onConnectClick = async () => {
    if (!email) {
      toast({
        title: "Clinic email required",
        description: "Clinic email is required to initiate Google authentication.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Pass email and clinicId to the auth helper so backend can associate tokens
    initiateAuth({ email, clinicId });
    fetchStatus();
  };

  const onDisconnectClick = async () => {
    revokeAuth();
    fetchStatus();

  };

  // Helpers for UI state
  const getDotColor = () => {
    if (!status) return "gray.400";
    if (status.authenticated) {
      return status.isExpiringSoon ? "yellow.400" : "green.400";
    }
    if (status.reason) return "red.400";
    return "gray.400";
  };

  const renderNotConnected = () => (
    <VStack align="start" spacing={4}>
      <Tag colorScheme="red" size="lg">
        ❌ Not Connected
      </Tag>
      <Text>
        Enable automatic Google Meet links for online appointments. When
        connected, appointments booked as "online" will automatically include a
        Meet link and calendar event.
      </Text>
      <HStack>
        <Button
          leftIcon={<MdLink />}
          colorScheme="brand"
          onClick={onConnectClick}
          isLoading={isAuthLoading}
        >
          Connect Google Calendar
        </Button>
        <Button
          onClick={fetchStatus}
          leftIcon={<MdRefresh />}
          isLoading={isLoadingStatus}
        >
          Refresh
        </Button>
      </HStack>
    </VStack>
  );

  const renderConnected = () => {
    const isExpiringSoon = Boolean(status?.requiresReauth);
    const expiresInSeconds = Number(status?.expiresIn) || 0;
    const expiresInMinutes =
      expiresInSeconds > 0 ? Math.round(expiresInSeconds / 60) : "—";
    const tagScheme = isExpiringSoon ? "yellow" : "green";
    const tagText = isExpiringSoon ? "Expiring Soon" : "Connected";

    return (
      <VStack align="start" spacing={4}>
        <Tag colorScheme={tagScheme} size="lg">
          {isExpiringSoon ? <MdWarning style={{ marginRight: 6 }} /> : "✅"}{" "}
          {tagText}
        </Tag>

        <Text>
          Connected account: <strong>{status?.email || email || "—"}</strong>
        </Text>

        {/* <Text>
          Expires in: <strong>{expiresInMinutes}</strong> minutes.
        </Text> */}

        {isExpiringSoon && (
          <Button
            leftIcon={<MdRefresh />}
            colorScheme="yellow"
            onClick={onConnectClick}
            isLoading={isAuthLoading}
            mb={2}
          >
            Re-authenticate Now
          </Button>
        )}

        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={fetchStatus}
            isLoading={isLoadingStatus}
          >
            Refresh
          </Button>
          {/* {!status?.authenticated && */}
            <Button
              leftIcon={<MdLinkOff />}
              colorScheme="red"
              variant="outline"
              onClick={onDisconnectClick}
              // isLoading={isAuthLoading}
            >
              Disconnect
          </Button>
            {/* } */}
        </HStack>
      </VStack>
    );
  };

  const renderErrorState = () => (
    <VStack align="start" spacing={4}>
      {/* <Tag colorScheme="red" size="lg">
        ❌
      </Tag> */}
      <Text>
        There was an issue with the Google integration. Reason:{" "}
        <strong>{status?.reason || "Unknown"}</strong>
        {status?.requiresReauth  &&
          ", please try re-authenticating."}
      </Text>
      <HStack>
        <Button
          leftIcon={<MdRefresh />}
          onClick={onConnectClick}
          isLoading={isAuthLoading}
        >
          Try Re-authenticating
        </Button>
        {/* {!status?.requiresReauth && */}
          <Button
          leftIcon={<MdLinkOff />}
          colorScheme="red"
          variant="outline"
          onClick={onDisconnectClick}
          isLoading={isAuthLoading}
          >
          Disconnect & Reset
        </Button>
        {/* } */}
      </HStack>
    </VStack>
  );

  // Only render this widget for admin users
  if (!isAdmin) {
    return null;
  }

  // Modal with full details
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      motionPreset="scale"
      returnFocusOnClose
    >
      <ModalOverlay />
      <ModalContent id="google-calendar-widget-modal">
        <ModalHeader display="flex" alignItems="center" gap={3}>
          <Box
            w="12px"
            h="12px"
            borderRadius="full"
            bg={getDotColor()}
            flex="0 0 auto"
          />
          <Text as="span">Google Meet Integration</Text>
          <ModalCloseButton position="absolute" right="8px" top="8px" />
        </ModalHeader>

        <ModalBody pb={2}>
          <Card>
            <Box p={4}>
              <Flex direction="column" align="start" gap={4}>
                {/* Loading / first load */}
                {isLoadingStatus && isFirstLoad && (
                  <Flex align="center" justify="center" w="100%" p={4}>
                    <Spinner />
                  </Flex>
                )}

                {/* If status not loaded or not authenticated */}
                {!isLoadingStatus && !status && renderNotConnected()}

                {/* Loaded and authenticated */}
                {!isLoadingStatus &&
                  status?.authenticated &&
                  renderConnected()}

                {/* Loaded but has error / requires reauth */}
                {!isLoadingStatus &&
                  status &&
                  !status.authenticated &&
                  status.reason &&
                  renderErrorState()}
              </Flex>
            </Box>
          </Card>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
